/*
 * File: ResourceCard.jsx
 * Project: our-wave-stories-app
 *
 * Created by Brendan Michaelsen on February 4, 2022 at 4:30 PM
 * Copyright © 2022 Our Wave, Inc. All rights reserved.
 *
 * Last Modified: June 5, 2025 at 8:51 AM
 * Modified By: Brendan Michaelsen
 */

/**
 * Imports
 */

// Modules
import React, {
	memo,
	useEffect, useMemo, useRef, useState
} from 'react';
import PropTypes from 'prop-types';
import { Trans, useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

// Utilities
import { generateShortFormContentPlaceholder, generateShortTitlePlaceholder } from '../../utilities/placeholder';
import { formatPhoneNumber } from '../../../utilities/phone';

// Components
import { Typography } from '../Typography';
import { ActionModule } from '../ActionModule';
import { LocaleLink } from '../LocaleLink';
import { ClampContent } from '../ClampContent';

// Constants
import {
	AVAILABLE_I18N_NAMESPACES, REFERRAL_SOURCES, SHARE_OPTIONS, SOURCES
} from '../../../constants/base.constants';

// Styles
import * as S from './ResourceCard.styles';


/**
 * Component
 */

export const ResourceCard = memo(({
	className, resource, source, isPlaceholder, titleTag, index
}) => {

	// Get current partner from hook
	const partner = useSelector((state) => state.partner.value);

	// Get current locale from hook
	const locale = useSelector((state) => state.locale.value);

	// Generate placeholder
	const namePlaceholder = useMemo(() => generateShortTitlePlaceholder(index), [index]);
	const descriptionPlaceholder = useMemo(() => generateShortFormContentPlaceholder(index), [index]);

	// Create state
	const [showCallWidget, setShowCallWidget] = useState(false);
	const [showMessageWidget, setShowMessageWidget] = useState(false);
	const [showEmailWidget, setShowEmailWidget] = useState(false);
	const [showVisitWidget, setShowVisitWidget] = useState(false);
	const [showShareWidget, setShowShareWidget] = useState(false);

	// Create refs
	const callContainerRef = useRef();
	const messageContainerRef = useRef();
	const emailContainerRef = useRef();
	const visitContainerRef = useRef();
	const shareContainerRef = useRef();
	const showCallContainerRef = useRef();
	const showMessageContainerRef = useRef();
	const showEmailContainerRef = useRef();
	const showVisitContainerRef = useRef();
	const showShareWidgetRef = useRef();

	// Get translation hook
	const [t] = useTranslation(AVAILABLE_I18N_NAMESPACES.CLIENT);

	// Handle call actions
	const callNumber = (payload) => {

		// Build url
		const url = `tel:${payload.number}`;

		// Build analytics url
		const analyticsUrl = `/_external/link?url=${encodeURIComponent(url)}&resourceId=${resource.resource_id}&localeId=${locale.localeId}&countryId=${locale.countryId}&src=${REFERRAL_SOURCES.WEBSITE_CARD}`;

		// Open url
		window.open(analyticsUrl, '_blank');
	};

	const callAction = () => {
		if (resource.phone_numbers.length > 1) {
			setShowCallWidget(true);
		} else {
			callNumber(resource.phone_numbers[0]);
		}
	};

	// Handle message actions
	const messageNumber = (payload) => {

		// Build url
		const url = `sms:${payload.number}`;

		// Build analytics url
		const analyticsUrl = `/_external/link?url=${encodeURIComponent(url)}&resourceId=${resource.resource_id}&localeId=${locale.localeId}&countryId=${locale.countryId}&src=${REFERRAL_SOURCES.WEBSITE_CARD}`;

		// Open url
		window.open(analyticsUrl, '_blank');
	};

	const messageAction = () => {
		if (resource.sms_numbers.length > 1) {
			setShowMessageWidget(true);
		} else {
			messageNumber(resource.sms_numbers[0]);
		}
	};

	// Handle email actions
	const email = (payload) => {

		// Build url
		const url = `mailto:${payload.email}`;

		// Build analytics url
		const analyticsUrl = `/_external/link?url=${encodeURIComponent(url)}&resourceId=${resource.resource_id}&localeId=${locale.localeId}&countryId=${locale.countryId}&src=${REFERRAL_SOURCES.WEBSITE_CARD}`;

		// Open url
		window.open(analyticsUrl, '_blank');
	};

	const emailAction = () => {
		if (resource.emails.length > 1) {
			setShowEmailWidget(true);
		} else {
			email(resource.emails[0]);
		}
	};

	// Handle visit actions
	const visit = (payload) => {

		// Build url
		const url = payload.url[locale.localeBaseId];

		// Build analytics url
		const analyticsUrl = `/_external/link?url=${encodeURIComponent(url)}&resourceId=${resource.resource_id}&localeId=${locale.localeId}&countryId=${locale.countryId}&src=${REFERRAL_SOURCES.WEBSITE_CARD}`;

		// Open url
		window.open(analyticsUrl, '_blank');
	};

	const visitAction = () => {
		if (resource.url_locales.length > 1) {
			setShowVisitWidget(true);
		} else {
			visit(resource.url_locales[0]);
		}
	};

	// Handle share flow
	const handleShare = () => {
		setShowShareWidget(!showShareWidget);
	};

	// Handle actions on ref change
	useEffect(() => {

		// Define click outside function
		function handleClickOutside(event) {
			if (showCallContainerRef.current && callContainerRef.current && !callContainerRef.current.contains(event.target)) {
				setShowCallWidget(false);
			}
			if (showMessageContainerRef.current && messageContainerRef.current && !messageContainerRef.current.contains(event.target)) {
				setShowMessageWidget(false);
			}
			if (showEmailContainerRef.current && emailContainerRef.current && !emailContainerRef.current.contains(event.target)) {
				setShowEmailWidget(false);
			}
			if (showVisitContainerRef.current && visitContainerRef.current && !visitContainerRef.current.contains(event.target)) {
				setShowVisitWidget(false);
			}
			if (showShareWidgetRef.current && shareContainerRef.current && !shareContainerRef.current.contains(event.target)) {
				setShowShareWidget(false);
			}
		}

		// Bind the event listener
		document.addEventListener('mousedown', handleClickOutside);

		// Return effect
		return () => { // Unbind the event listener on clean up
			document.removeEventListener('mousedown', handleClickOutside);
		};

	}, [shareContainerRef]);

	// Handle actions on widget state

	useEffect(() => { showCallContainerRef.current = showCallWidget; }, [showCallWidget]);
	useEffect(() => { showMessageContainerRef.current = showMessageWidget; }, [showMessageWidget]);
	useEffect(() => { showEmailContainerRef.current = showEmailWidget; }, [showEmailWidget]);
	useEffect(() => { showVisitContainerRef.current = showVisitWidget; }, [showVisitWidget]);
	useEffect(() => { showShareWidgetRef.current = showShareWidget; }, [showShareWidget]);

	// Handle actions on component load
	useEffect(() => {}, []);

	// Render component
	return (
		<S.Wrapper className={className} $isPlaceholder={isPlaceholder}>

			{/* Resource Link */}
			<LocaleLink to={`/resource/${resource?.slug}`}>

				{/* Logo Image */}
				{resource?.logo_image && (
					<S.Logo src={resource?.logo_image} className={isPlaceholder && 'shimmer shimmer-element'} />

				)}

				{/* Resource Content */}
				<S.CardInner>
					<ClampContent clamp={2}>
						<Typography tag={titleTag} tagStyle="h5" weight="bold" className={isPlaceholder ? 'shimmer shimmer-element' : null}>
							{resource?.name_locales?.[locale.localeBaseId] || namePlaceholder}
						</Typography>
					</ClampContent>
					<ClampContent clamp={4} className={isPlaceholder ? 'shimmer shimmer-element' : null}>
						<Typography tag="p" variation="2">
							{resource?.description_locales?.[locale.localeBaseId] || descriptionPlaceholder}
						</Typography>
					</ClampContent>
				</S.CardInner>

			</LocaleLink>

			{/* Action Bar */}
			{!isPlaceholder && (
				<S.ActionBar>

					{/* Action - Call */}
					{resource?.has_phone && (
						<S.ActionContainer ref={callContainerRef}>

							{/* Call Button */}
							<ActionModule
								onClick={callAction}
							>
								<FontAwesomeIcon icon={['far', 'phone']} />
								<Typography tag="span">
									<Trans t={t} i18nKey="actions.call">
										Call
									</Trans>
								</Typography>
							</ActionModule>

							{/* Action Widget */}
							<S.ActionWidget
								className={showCallWidget ? 'animate show' : 'animate'}
							>
								<S.ActionWidgetContainer>
									{resource?.phone_numbers?.map((phoneInfo) => (
										<S.ActionWidgetRow onClick={() => { callNumber(phoneInfo); }} key={phoneInfo.number} className="animate">
											<S.IconContainer>
												<S.NavigationIcon icon={['far', 'phone']} />
											</S.IconContainer>
											<S.WidgetContentContainer>
												<Typography
													tag="p"
													variation="2"
													weight="semibold"
												>
													{formatPhoneNumber(phoneInfo.number)}
												</Typography>
												{phoneInfo.description[locale.localeBaseId] && (
													<Typography
														tag="p"
														variation="3"
														weight="regular"
													>
														{phoneInfo.description[locale.localeBaseId]}
													</Typography>
												)}
											</S.WidgetContentContainer>
										</S.ActionWidgetRow>
									))}
								</S.ActionWidgetContainer>
							</S.ActionWidget>
						</S.ActionContainer>
					)}

					{/* Action - Message */}
					{resource?.has_sms && (
						<S.ActionContainer ref={messageContainerRef}>

							{/* Message Button */}
							<ActionModule
								onClick={messageAction}
							>
								<FontAwesomeIcon icon={['far', 'message']} />
								<Typography tag="span">
									<Trans t={t} i18nKey="actions.sendSMS">
										Send SMS
									</Trans>
								</Typography>
							</ActionModule>

							{/* Action Widget */}
							<S.ActionWidget
								className={showMessageWidget ? 'animate show' : 'animate'}
							>
								<S.ActionWidgetContainer>
									{resource?.sms_numbers?.map((smsInfo) => (
										<S.ActionWidgetRow onClick={() => { messageNumber(smsInfo); }} key={smsInfo.number} className="animate">
											<S.IconContainer>
												<S.NavigationIcon icon={['far', 'message']} />
											</S.IconContainer>
											<S.WidgetContentContainer>
												<Typography
													tag="p"
													variation="2"
													weight="semibold"
												>
													{formatPhoneNumber(smsInfo.number)}
												</Typography>
												{smsInfo.description[locale.localeBaseId] && (
													<Typography
														tag="p"
														variation="3"
														weight="regular"
													>
														{smsInfo.description[locale.localeBaseId]}
													</Typography>
												)}
											</S.WidgetContentContainer>
										</S.ActionWidgetRow>
									))}
								</S.ActionWidgetContainer>
							</S.ActionWidget>
						</S.ActionContainer>
					)}

					{/* Action - Email */}
					{resource?.has_email && (
						<S.ActionContainer ref={emailContainerRef}>

							{/* Email Button */}
							<ActionModule onClick={emailAction}>
								<FontAwesomeIcon icon={['far', 'envelope']} />
								<Typography tag="span">
									<Trans t={t} i18nKey="actions.sendEmail">
										Send email
									</Trans>
								</Typography>
							</ActionModule>

							{/* Action Widget */}
							<S.ActionWidget
								className={showEmailWidget ? 'animate show' : 'animate'}
							>
								<S.ActionWidgetContainer>
									{resource?.emails?.map((emailInfo) => (
										<S.ActionWidgetRow onClick={() => { email(emailInfo); }} key={emailInfo.email} className="animate">
											<S.IconContainer>
												<S.NavigationIcon icon={['far', 'envelope']} />
											</S.IconContainer>
											<S.WidgetContentContainer>
												<Typography
													tag="p"
													variation="2"
													weight="semibold"
												>
													{emailInfo.email}
												</Typography>
												{emailInfo.description[locale.localeBaseId] && (
													<Typography
														tag="p"
														variation="3"
														weight="regular"
													>
														{emailInfo.description[locale.localeBaseId]}
													</Typography>
												)}
											</S.WidgetContentContainer>
										</S.ActionWidgetRow>
									))}
								</S.ActionWidgetContainer>
							</S.ActionWidget>
						</S.ActionContainer>
					)}

					{/* Action - Visit */}
					{resource?.has_url && (
						<S.ActionContainer ref={visitContainerRef}>

							{/* Visit Button */}
							<ActionModule onClick={visitAction}>
								<FontAwesomeIcon icon={['far', 'arrow-up-right']} />
								<Typography tag="span">
									<Trans t={t} i18nKey="actions.visit">
										Visit
									</Trans>
								</Typography>
							</ActionModule>

							{/* Action Widget */}
							<S.ActionWidget
								className={showVisitWidget ? 'animate show' : 'animate'}
							>
								<S.ActionWidgetContainer>
									{resource?.url_locales?.map((urlInfo) => (
										<S.ActionWidgetRow onClick={() => { visit(urlInfo); }} key={urlInfo.url[locale.localeBaseId]} className="animate">
											<S.IconContainer>
												<S.NavigationIcon icon={['far', 'arrow-up-right']} />
											</S.IconContainer>
											<S.WidgetContentContainer>
												<Typography
													tag="p"
													variation="2"
													weight="semibold"
												>
													<Trans t={t} i18nKey="actions.visitWebsite">
														Visit website
													</Trans>
												</Typography>
												{urlInfo.description[locale.localeBaseId] && (
													<Typography
														tag="p"
														variation="3"
														weight="regular"
													>
														{urlInfo.description[locale.localeBaseId]}
													</Typography>
												)}
											</S.WidgetContentContainer>
										</S.ActionWidgetRow>
									))}
								</S.ActionWidgetContainer>
							</S.ActionWidget>
						</S.ActionContainer>
					)}

					{/* Action - Share */}
					<S.ActionContainer ref={shareContainerRef}>

						{/* Share Button */}
						<ActionModule
							onClick={handleShare}
						>
							<FontAwesomeIcon className="share" icon={['far', 'share']} />
							<Typography tag="span">
								<Trans t={t} i18nKey="actions.share">
									Share
								</Trans>
							</Typography>
						</ActionModule>

						{/* Share Widget */}
						<S.ShareWidget
							className={showShareWidget ? 'animate show' : 'animate'}
							fromBottom={false}
							closeWidget={() => { setShowShareWidget(false); }}
							source={{
								type: SOURCES.RESOURCE,
								id: resource?.resource_id
							}}
							content={{
								title: t('resourceCard.shareTitle'),
								url: `https://community.ourwave.org${locale.localePath}/resource/${resource?.slug}`
							}}
							items={[
								SHARE_OPTIONS.WHATSAPP,
								SHARE_OPTIONS.FACEBOOK,
								SHARE_OPTIONS.LINK,
								SHARE_OPTIONS.TWITTER,
								SHARE_OPTIONS.LINKEDIN,
								SHARE_OPTIONS.REDDIT,
								SHARE_OPTIONS.PINTEREST,
								SHARE_OPTIONS.EMAIL
							]}
						/>

					</S.ActionContainer>

				</S.ActionBar>
			)}
		</S.Wrapper>
	);
});


/**
 * Configuration
 */

ResourceCard.displayName = 'ResourceCard';
ResourceCard.propTypes = {
	className: PropTypes.string,
	resource: PropTypes.shape(),
	source: PropTypes.oneOf(Object.values(SOURCES)),
	isPlaceholder: PropTypes.bool,
	titleTag: PropTypes.string,
	index: PropTypes.number
};
ResourceCard.defaultProps = {
	className: null,
	resource: {},
	source: null,
	isPlaceholder: false,
	titleTag: 'h5',
	index: 0
};
